/**
 * Device Replacement Mutations
 * React Query mutations สำหรับการเปลี่ยนอุปกรณ์
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import deviceReplacementService, {
  DeviceReplacementCreate,
  DeviceReplacementResponse,
} from '../../services/deviceReplacementService';
import { showSuccess, showError } from '../../utils/toast';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

/**
 * Hook สำหรับเปลี่ยนอุปกรณ์
 */
export const useReplaceDevice = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DeviceReplacementResponse,
    Error,
    { rentalItemId: number; data: DeviceReplacementCreate }
  >({
    mutationFn: ({ rentalItemId, data }) =>
      deviceReplacementService.replaceDevice(rentalItemId, data),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['rentals'] });
      queryClient.invalidateQueries({ queryKey: ['rental'] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['sims'] });
      queryClient.invalidateQueries({ queryKey: ['device-replacements'] });
      // เคสซ่อมบำรุงอาจถูกผูก (maintenance_id) — invalidate query ใดๆ ที่มีคำว่า 'maintenance'
      // อยู่ใน key (เช่น queryKeys.devices.maintenance(id) ที่แท็บซ่อมบำรุงในหน้าอุปกรณ์ใช้)
      // หน้ารายการ/รายละเอียดเคสซ่อม (MaintenancePage/MaintenanceViewPage) ใช้ manual fetch ไม่ผ่าน
      // react-query จึงไม่ต้อง invalidate เพิ่ม — หน้าเหล่านั้น refresh เองหลัง modal สำเร็จ
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('maintenance'),
      });
      showSuccess('เปลี่ยนอุปกรณ์สำเร็จ');
    },
    onError: (error: Error) => {
      // user bug Tiwakorn 2026-08-30 (maintenance #309): เดิมใช้ error.message = ข้อความ axios
      // "Request failed with status code 400" → ผู้ใช้ไม่เห็นเหตุผลจริงจาก backend
      // (เช่น "SIM ที่เลือกไม่พร้อมใช้งาน: 08x อยู่ในสถานะ กำลังใช้งาน (ใช้อยู่ในสัญญา RNT-...)")
      // → ใช้ helper กลางอ่าน KRP envelope / FastAPI detail แบบเดียวกับ mutation hook อื่น
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการเปลี่ยนอุปกรณ์'));
    },
  });
};

export default {
  useReplaceDevice,
};
